// ConfigAudit.tsx
import { useState, useCallback, useEffect } from "react";
import { Loader2, Info, CheckCircle, } from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Revision {
    id: number;
    time: string;
    admin: string;
    comment: string;
    time_unix: number;
    version_id: string;
}

interface ConfigAuditData {
    revisions: Revision[];
    description: string;
    backup_status: string;
    last_change_by: string;
    revision_count: number;
    last_change_note: string;
    last_change_time: string;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ConfigAudit({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [data, setData] = useState<ConfigAuditData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "config_audit",
                    }),
                    limit: 1,
                },
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setData(parsed);
            } else showError("Failed to load config audit");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return (
        <div className="flex items-center justify-center gap-2 py-20">
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">Loading config audit...</span>
        </div>
    );

    if (!data) return (
        <div className="flex flex-col items-center gap-2 py-16 opacity-40">
            <Info size={22} className="text-gray-200" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No audit data available</span>
        </div>
    );

    // Oldest revision date
    const oldestDate = data.revisions.length > 0
        ? data.revisions[data.revisions.length - 1].time.split(" ")[0]
        : "";

    return (
        <div className="space-y-4">

            {/* 3 Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                {/* Total Revisions */}
                <div className="bg-gray-800 border border-gray-800 rounded-xl px-5 py-2">
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Total Revisions</p>
                    <p className="text-[20px] font-black text-blue-400 leading-none">{data.revision_count}</p>
                    {oldestDate && (
                        <p className="text-[10px] text-gray-200 mt-1.5">Since {oldestDate}</p>
                    )}
                </div>

                {/* Backup Status */}
                <div className="bg-gray-800 border border-gray-800 rounded-xl px-5 py-2">
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Backup Status</p>
                    <div className="flex items-center gap-1.5">
                        <CheckCircle size={14} className={data.backup_status === "Available" ? "text-green-400" : "text-gray-200"} />
                        <p className={`text-[20px] font-black leading-none ${data.backup_status === "Available" ? "text-green-400" : "text-gray-400"}`}>
                            {data.backup_status || "-"}
                        </p>
                    </div>
                    <p className="text-[10px] text-gray-200 mt-1.5">
                        Latest: {data.last_change_time.split(" ")[0] || "—"}
                    </p>
                </div>

                {/* Last Change By */}
                <div className="bg-gray-800 border border-gray-800 rounded-xl px-5 py-2">
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Last Change By</p>
                    <p className="text-[20px] font-black text-orange-400 leading-none font-mono">{data.last_change_by}</p>
                    <p className="text-[10px] text-gray-200 mt-1.5">{data.last_change_note || "-"}</p>
                </div>
            </div>

            {/* Revision Timeline */}
            <div className="bg-gray-800 border border-gray-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-800">
                    <h3 className="text-[13px] font-bold text-white">Revision timeline</h3>
                </div>

                <div className="px-5 py-1">
                    <div className="relative">
                        {/* Vertical line */}
                        {data.revisions.length > 0 && (
                            <div className="absolute left-[5px] top-2 bottom-2 w-px bg-gray-600" />
                        )}

                        <div className="space-y-0">
                            {data.revisions.length === 0 && (
                                <div className="flex items-center justify-center gap-2 py-16 opacity-40">
                                    <Info size={22} className="text-gray-200" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No audit data available</span>
                                </div>
                            )}
                            {data.revisions.map((rev, _idx) => (
                                <div key={rev.id} className="relative flex gap-4 group">

                                    {/* Dot */}
                                    <div className="relative z-10 mt-[14px] shrink-0">
                                        <div className={`w-[11px] h-[11px] rounded-full border-2 transition-colors bg-blue-500 border-blue-400`} />
                                    </div>

                                    {/* Content */}
                                    <div className={`flex-1 py-3 border-b border-gray-800/50 last:border-0 group-hover:bg-gray-800/20 -mx-1 px-1 rounded transition-colors`}>
                                        {/* Time + Revision number */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] text-gray-200 font-mono">{rev.time}</span>
                                            <span className="text-gray-700">·</span>
                                            <span className="text-[10px] font-bold text-white">{rev.comment}</span>

                                            <span className="text-[10px] font-bold text-gray-400">
                                                Revision {rev.id}
                                            </span>
                                            <span className="text-[10px] text-blue-300 font-mono">by {rev.admin}</span>
                                        </div>



                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
